import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Define theme types
interface ThemeColors {
  name: string;
  primary: string;
  background: string;
  card: string;
  text: string;
  muted: string;
  accent: string;
  border: string;
  description: string;
}

interface ThemeContextType {
  currentTheme: ThemeColors;
  setTheme: (themeName: string) => void;
  availableThemes: ThemeColors[];
}

// Define the available themes
const themeOptions: Record<string, ThemeColors> = {
  calm: {
    name: 'calm',
    primary: '#4a90e2',
    background: '#f5f9ff',
    card: '#ffffff',
    text: '#333333',
    muted: '#6c757d',
    accent: '#b8d4ff',
    border: '#e1e7ef',
    description: 'Soft blues to promote tranquility and reduce visual stress'
  },
  focus: {
    name: 'focus',
    primary: '#6e7582',
    background: '#f7f7f7',
    card: '#ffffff',
    text: '#333333',
    muted: '#6c757d',
    accent: '#d9dbe0',
    border: '#e9e9e9',
    description: 'Neutral colors to minimize distractions and help with concentration'
  },
  comfort: {
    name: 'comfort',
    primary: '#e2884a',
    background: '#fff9f5',
    card: '#ffffff',
    text: '#333333',
    muted: '#6c757d',
    accent: '#ffdfc2',
    border: '#efe1d7',
    description: 'Warm colors that provide a sense of security and emotional comfort'
  },
  energize: {
    name: 'energize',
    primary: '#e34c67',
    background: '#fff5f7',
    card: '#ffffff',
    text: '#333333',
    muted: '#6c757d',
    accent: '#ffc2d0',
    border: '#efdbdf',
    description: 'Brighter colors that can help with alertness and attention'
  },
  soothe: {
    name: 'soothe',
    primary: '#4abe7c',
    background: '#f5fff9',
    card: '#ffffff',
    text: '#333333',
    muted: '#6c757d',
    accent: '#c2ffd9',
    border: '#d7efe1',
    description: 'Gentle greens that are calming and promote relaxation'
  },

  soft: {
    name: 'soft',
    primary: '#c8a5f7',
    background: '#faf5ff',
    card: '#ffffff',
    text: '#333333',
    muted: '#6c757d',
    accent: '#f3e6ff',
    border: '#ecdeff',
    description: 'Extra soft pastels to accommodate visual sensitivity needs'
  }
};

// Create the context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Provider component
export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  // Initialize with a default theme or from localStorage
  const [currentTheme, setCurrentTheme] = useState<ThemeColors>(() => {
    // Try to get the saved theme from localStorage
    const savedTheme = localStorage.getItem('userTheme');
    // If the saved theme is 'night' (now removed), default to 'calm'
    if (savedTheme === 'night') {
      localStorage.setItem('userTheme', 'calm');
      return themeOptions.calm;
    }
    // Return the saved theme or default to 'calm'
    return savedTheme && themeOptions[savedTheme] ? themeOptions[savedTheme] : themeOptions.calm;
  });

  // Function to set the theme
  const setTheme = (themeName: string) => {
    if (themeOptions[themeName]) {
      setCurrentTheme(themeOptions[themeName]);
      localStorage.setItem('userTheme', themeName);
    }
  };
  
  // Helper function to convert hex to HSL
  const hexToHsl = (hex: string) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
        default: h = 0;
      }
      h /= 6;
    }

    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  };

  // Apply theme to CSS variables when theme changes
  useEffect(() => {
    const root = document.documentElement;
    
    // Set Tailwind CSS variables in HSL format
    root.style.setProperty('--primary', hexToHsl(currentTheme.primary));
    root.style.setProperty('--background', hexToHsl(currentTheme.background));
    root.style.setProperty('--card', hexToHsl(currentTheme.card));
    root.style.setProperty('--muted', hexToHsl(currentTheme.muted));
    root.style.setProperty('--accent', hexToHsl(currentTheme.accent));
    root.style.setProperty('--border', hexToHsl(currentTheme.border));
    
    // Set foreground colors (contrasting text colors)
    root.style.setProperty('--primary-foreground', '0 0% 98%'); // white for primary
    root.style.setProperty('--card-foreground', hexToHsl(currentTheme.text));
    root.style.setProperty('--muted-foreground', hexToHsl(currentTheme.muted));
    root.style.setProperty('--accent-foreground', hexToHsl(currentTheme.text));
    root.style.setProperty('--foreground', hexToHsl(currentTheme.text));
    
    // Set additional Tailwind variables
    root.style.setProperty('--popover', hexToHsl(currentTheme.card));
    root.style.setProperty('--popover-foreground', hexToHsl(currentTheme.text));
    root.style.setProperty('--secondary', hexToHsl(currentTheme.accent));
    root.style.setProperty('--secondary-foreground', hexToHsl(currentTheme.text));
    root.style.setProperty('--input', hexToHsl(currentTheme.border));
    root.style.setProperty('--ring', hexToHsl(currentTheme.primary));
    
    // Set destructive colors (red variants)
    root.style.setProperty('--destructive', '0 84% 60%');
    root.style.setProperty('--destructive-foreground', '0 0% 98%');
  }, [currentTheme]);

  return (
    <ThemeContext.Provider 
      value={{ 
        currentTheme, 
        setTheme, 
        availableThemes: Object.values(themeOptions)
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

// Hook for using the theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Export theme options for access outside of context
export { themeOptions };